/**
 * firmaSessionService.js — "Giriş yapan hesap hangi firma?" sorusunun tek cevabı.
 *
 * Bugüne kadar her Firma-tarafı sayfa kendi `getCurrentFirmaId()` kopyasını taşıyordu
 * (`localStorage.user.firmaId` → yoksa mock id) ve canlı fuarlarda
 * `TEST_DECORATOR_FIRMA_ID = 49858` sabitine düşüyordu. Sebebi backend tarafındaki bir
 * boşluktu: test hesabının `user_to_firma` satırı yoktu, dolayısıyla
 * `GET /firma/userFirma/{userId}` "No firma is linked to user 22236" diyordu
 * (bkz. util/knowledgebase/local-backend-testing.md, 2026-08-29 notu).
 *
 * ✅ 2026-09-04: satır artık VAR — yerelde doğrulandı, `GET /firma/userFirma/22236` → `49858`.
 * Yani hesabın firması gerçekten çözülebiliyor ve hardcode'a gerek kalmadı.
 *
 * ⚠️ NEDEN ARTIK ÖNEMLİ: 2026-09-03'te `PersonnelController`'ın 14 ucu ve tüm
 * `firma-taseron` ucu `user_to_firma`'ya göre TENANCY kontrolü yapıyor. Yanlış bir
 * `firmaId` göndermek artık boş liste değil **403** demek. Bu yüzden yeni sayfalar
 * sabit yerine bu çözücüyü kullanır.
 *
 * Eski sayfalardaki 8 `getCurrentFirmaId()` kopyası KASITLI olarak elle değiştirilmedi —
 * her biri farklı bir mock/forceLive köprüsüne bağlı ve hepsi ayrı ayrı test edilmeli.
 * Yeni bir sayfa yazarken bu dosyayı kullanın; eski bir sayfaya dokunurken oradaki
 * kopyayı buna taşıyın.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

/**
 * Firma Moderatör / Admin rolleri — `user_to_user_group`. #307'den beri tenancy kararı BUNUNLA
 * verilmiyor (bkz. isStaffGroupInfo); yalnızca "bu iki rol" anlamında okunur.
 */
export const FIRMA_GROUP_IDS = [9, 19];

/**
 * Test dekoratörü. #311'e (2026-10-11) kadar çözümleme başarısız olursa son çareydi; artık hiçbir yerde
 * yedek değil (bkz. resolveCurrentFirmaId). Yalnızca eski sayfaların test fuarı köprüsü için duruyor.
 */
export const FALLBACK_FIRMA_ID = 49858;

// Oturum boyunca değişmiyor; her sayfa açılışında tekrar sormanın anlamı yok.
let _cache = null;

function readUserId() {
  return localStorage.getItem('userId') || null;
}

/**
 * Oturumdaki kullanıcının firma id'si.
 *
 * Sıra: (1) backend'e sor — tek doğru kaynak · (2) `localStorage.user.firmaId` —
 * eski giriş akışının bıraktığı değer. İkisi de yoksa **null** (#311): çağıran HATA gösterir.
 * WAS (until #311): (3) `FALLBACK_FIRMA_ID` — firması çözülemeyen her hesap test dekoratörünün
 * (49858) portalını görüyordu; #315'ten beri de o firmanın verisine 403 alıyordu.
 *
 * Backend düz sayı döner (`49858`), zarf değil. Kullanıcının firması yoksa gövde
 * "No firma is linked to user …" metnidir — sayı olmadığı için elenir.
 * GET /api/v1/firma/userFirma/{userId}
 */
export async function resolveCurrentFirmaId({ force = false } = {}) {
  if (_cache && !force) return _cache;

  const userId = readUserId();
  if (userId) {
    try {
      const response = await axios.get(`${JAVA_BACKEND_API_URL}/firma/userFirma/${userId}`, {
        headers: getAuthHeader(),
      });
      const value = Number(response.data);
      if (Number.isFinite(value) && value > 0) {
        _cache = value;
        return value;
      }
    } catch {
      // 404/403/"No firma is linked" — aşağıdaki yedeklere düş.
    }
  }

  try {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.firmaId) {
      _cache = Number(user.firmaId);
      return _cache;
    }
  } catch {
    /* localStorage bozuksa yedeğe düş */
  }

  // WAS (until #311):
  // _cache = FALLBACK_FIRMA_ID;
  // return _cache;
  return null;
}

/**
 * Oturumdaki kullanıcı bir firma hesabı mı (SAKA personeli değil mi)?
 * Grup `localStorage.groupInfo.id`'de duruyor (MainContext girişte yazıyor,
 * AuthProtected.js aynı anahtarı okuyor).
 * ⚠️ Grup satırı OLMAYAN kullanıcı backend tarafında da FİRMA sayılır — aynı varsayım.
 */
export function getCurrentUserGroupId() {
  try {
    const groupInfo = JSON.parse(localStorage.getItem('groupInfo'));
    const id = Number(groupInfo?.id);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

/**
 * Backlog #307 (2026-10-10, Bilge): personel = denetmen rolü. Backend'in tek kuralı
 * (UserGroupConstant.isStaff, #329): SuperAdmin (grup 1) ya da `user_group.is_denetmen = 1` olan rol.
 * Geri kalan herkes — Firma Moderatör/Admin (9/19), Organizatör rolleri (14-18), İşyeri Hekimi /
 * İSG Uzmanı (20/21), rolü olmayan hesap — FİRMA tarafıdır ve girişten sonra firma portalına gider.
 *
 * Kaynak: girişte saklanan `groupInfo` (GET /users/user/{id}/group). Yeni backend `staff` alanını
 * gönderiyor; onu bilmeyen eski bir yanıt / önbellek için aynı kural rol satırından hesaplanır.
 */
export function isStaffGroupInfo(groupInfo) {
  if (!groupInfo) return false;
  if (typeof groupInfo.staff === 'boolean') return groupInfo.staff;
  const id = Number(groupInfo.id ?? groupInfo.userGroupEntity?.id);
  const isDenetmen = Number(groupInfo.userGroupEntity?.isDenetmen);
  return id === 1 || isDenetmen === 1;
}

function readGroupInfo() {
  try {
    return JSON.parse(localStorage.getItem('groupInfo'));
  } catch {
    return null;
  }
}

export function isFirmaAccount() {
  // WAS (until #307): sabit id listesi — Organizatör rolleri (14-18) bu yüzden personel sayılıyordu.
  // const groupId = getCurrentUserGroupId();
  // if (groupId === null) return true;
  // return FIRMA_GROUP_IDS.includes(groupId);
  return !isStaffGroupInfo(readGroupInfo());
}

/** SAKA personeli mi — yalnızca denetmen rolleri ve SuperAdmin (#307; backend'in `isStaff` kuralı). */
export function isStaffAccount() {
  return !isFirmaAccount();
}

/** Testler / hesap değişimi için önbelleği boşaltır. */
export function clearFirmaIdCache() {
  _cache = null;
}
